import { usePeriod } from '@/app/context/PeriodContext';
import { SharingCollaboratorResponse } from '@/app/models/sharingDetails';
import { useFindAllCollaboratorProjectExpenseQuery } from '@/app/services/SharingDetails';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import formatterPercentualValue from '@/app/utils/percentualUtils';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Collapse,
  IconButton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import SharingCollaboratorDetail from './SharingCollaboratorDetail';

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 'bold',
  border: 'none',
  padding: theme.spacing(1),
}));

const RowTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 13,
  backgroundColor: theme.palette.grey[200],
  cursor: 'pointer',
  border: 'none',
  padding: theme.spacing(1),
}));

type RuleClassificationTableGridParams = {
  rows: SharingCollaboratorResponse[];
};

function Row({ row }: { row: SharingCollaboratorResponse }) {
  const { code } = useParams();
  const { selectedPeriod } = usePeriod();

  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation('sharingDetails');
  const { data } = useFindAllCollaboratorProjectExpenseQuery(
    {
      data: selectedPeriod?.date as string,
      badge: row.badge as string,
      code: code || '',
    },
    {
      skip: !selectedPeriod?.date,
    },
  );
  const navigate = useNavigate();

  return (
    <>
      <TableRow hover sx={{ '& > *': { bd: 0 } }}>
        <RowTableCell onClick={() => setOpen(!open)} colSpan={2}>
          <IconButton size="small">{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
          {`${row.badge} - ${row.name}`}
        </RowTableCell>
        <RowTableCell onClick={() => setOpen(!open)}>{`${formatterMonetaryValue(row.salary)}`}</RowTableCell>
        <RowTableCell onClick={() => setOpen(!open)} align="center">{`${formatterPercentualValue(
          row.percent,
        )}`}</RowTableCell>
        <RowTableCell onClick={() => setOpen(!open)}>{`${formatterMonetaryValue(row.value)}`}</RowTableCell>
        <RowTableCell align="center">
          <Tooltip title={t('detail.collaborator')} arrow placement="top">
            <Box sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center' }}>
              <IconButton
                size="small"
                onClick={() => {
                  navigate(`/sharingDetail/${code}/${row.badge}`);
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
              <Typography
                sx={{
                  cursor: 'pointer',
                  fontSize: 13,
                }}
                onClick={() => {
                  navigate(`/sharingDetail/${code}/${row.badge}`);
                }}
              >
                {t('detail.collaborator')}
              </Typography>
            </Box>
          </Tooltip>
        </RowTableCell>
      </TableRow>
      {open && (
        <TableRow sx={{ p: 0, m: 0, '& > *': { borderBottom: 0 } }}>
          <TableCell colSpan={6} sx={{ p: 0, m: 0, borderBottom: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box>
                <Table size="small" sx={{ width: '100%' }}>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ padding: 0 }}>
                        <SharingCollaboratorDetail rows={data || []} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function SharingTable({ rows }: RuleClassificationTableGridParams) {
  const { t } = useTranslation(['sharingDetails']);

  return (
    <TableContainer sx={{ height: '35rem' }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <HeaderTableCell colSpan={2}>{t('collaborator')}</HeaderTableCell>
            <HeaderTableCell>{t('salary.total')}</HeaderTableCell>
            <HeaderTableCell align="center">{t('percentual')}</HeaderTableCell>
            <HeaderTableCell>{t('paid')}</HeaderTableCell>
            <HeaderTableCell align="right">{t('actions')}</HeaderTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <Row key={row.badge} row={row} />
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="subtitle2">{t('no.data.found')}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
